import type { ReactNode } from 'react';
import { Section } from '../Panel.tsx';
import { StatusDot } from '../StatusDot.tsx';
import { NO_VALUE } from '../format.ts';
import { Unit } from '../Unit.tsx';
import { levelTone, type Tone } from '../state.ts';
import { Figure } from '../states/Figure.tsx';
import type { Read } from '../states/read.ts';
import { Icon } from '../../shell/Icon.tsx';
import styles from './charts.module.css';

/* ==========================================================================
   The channel rail.

   `auterion-flight-log-review.png`'s section index is the idea: every channel
   carries its own verdict dot, so the operator sees WHICH subsystem is bad
   before opening anything. Ours earns its width three times over — it is the
   health index, the legend, the visibility control AND the crosshair readout
   §5 asks for: "a playhead line plus a two-column aligned tooltip listing
   every series' value at that instant". A tooltip that has to be summoned is
   a readout the operator cannot scan; this is the same block, always on
   screen, already aligned.

   Visibility is a colour step and nothing else (`rerun-kissicp-blueprint-
   tree-viewer.png`): no eye icon appearing and disappearing, which would
   reflow every row the moment the pointer crossed one.

   The verdict is the WORST the channel reached anywhere in the window, not
   its value at the cursor — a battery that hit 8% an hour ago is a fact about
   this window even when the cursor is parked somewhere healthy. The value
   beside it is the cursor's, and the two are different questions.
   ========================================================================== */

export interface ChannelRow {
  key: string;
  /** Short enough to survive the rail's label column. */
  label: string;
  unit: string;
  verdict: 'ok' | 'warn' | 'bad' | 'unknown';
  /**
   * The extreme the window reached at this channel's dangerous end, already
   * formatted and already carrying its arrow — `↓5.9`, `↑94.1`. Set only
   * where the verdict is worse than ok, because on a healthy channel it is a
   * number nobody is asking for.
   *
   * This is what stops the dot being the sole carrier of the verdict: a red
   * dot beside a white `79.9 %` is a contradiction the operator has to
   * resolve from a tooltip, and colour is never allowed to be the only
   * encoding of a state.
   */
  peak?: string | undefined;
  /** Already formatted; `—` where the window has no reading at the cursor. */
  value: string;
  /** The threshold verdict of THAT value — the dot is the window's worst. */
  level: 'ok' | 'warn' | 'bad' | 'unknown';
  /** The value came from the newest reading, and that reading is not current. */
  stale: boolean;
  shown: boolean;
  hint: string;
}

/** One line of provenance: `Cadence  2m 24s`. */
export interface RailFact {
  key: string;
  label: string;
  value: string;
  /** A dim qualifier printed after the value — a unit, or a state word. Colour
      is never the only carrier of a fact's state (§ accessibility), so a row
      whose tone means something prints the word too. */
  suffix?: string;
  title: string;
  tone?: Tone;
  /** Value gets its own line under the label — for coordinates and paths. */
  wide?: boolean;
}

/** A labelled block of facts. The eyebrow's aside is the block's own state. */
export interface RailGroup {
  key: string;
  label: string;
  /** A node, so a block whose state IS an object can hand back a way to open
      it — `Source · jetrover-02` is the only reference to a robot on this
      surface, and it used to be an inert string. */
  aside: ReactNode;
  facts: readonly RailFact[];
}

export interface RobotRow {
  id: string;
  colour: string;
  tone: Tone;
  hollow: boolean;
  /** `Live`, `Stale`, `Offline` … the liveness word, as the API reports it. */
  liveness: string;
  /** How old this robot's newest reading in the window is — set only when
      that reading is not current, so a comparison says WHICH lane is behind
      and by how much rather than leaving the operator to read it off the
      trace's right end (§13 tell 24, §11.3's per-stream freshness). */
  age?: string | undefined;
  shown: boolean;
  /** The only series still drawn: hiding it would empty the instrument. */
  locked: boolean;
  /** The surface's subject: it is the crumb's robot and cannot be dropped. */
  primary: boolean;
}

export function ChannelRail({
  channels,
  robots,
  groups,
  onToggleChannel,
  onToggleRobot,
  onRemoveRobot,
  adder,
  read,
}: {
  channels: readonly ChannelRow[];
  robots: readonly RobotRow[];
  /**
   * THE READ THE ROSTER CAME FROM. `ROBOTS 0` counted from an unread
   * `/api/robots` is the rail reporting an empty fleet; the roster is a
   * measurement only once something answered.
   */
  read: Read;
  /** Provenance blocks: the cursor's reading, the window, the source. */
  groups: readonly RailGroup[];
  /** `additive` is ⇧/⌘-click: add to the current set instead of isolating. */
  onToggleChannel: (key: string, additive: boolean) => void;
  onToggleRobot: (id: string) => void;
  onRemoveRobot: (id: string) => void;
  /** The control that adds a robot to the comparison. */
  adder: ReactNode;
}) {
  const shown = channels.filter((c) => c.shown).length;

  return (
    <div className={styles.rail}>
      <Section
        label="Channels"
        aside={
          <span
            className={styles.railCount}
            title={
              'Channels drawn on the instrument, of the channels the contract '
              + 'carries. Click one to isolate it; shift-click to add.'
            }
          >
            {`${shown}/${channels.length}`}
          </span>
        }
      >
        <ul className={styles.railList}>
          {channels.map((c) => (
            <li key={c.key}>
              <button
                type="button"
                className={styles.railRow}
                data-shown={c.shown}
                aria-pressed={c.shown}
                title={
                  `${c.hint}\nWorst in this window: ${
                    c.peak === undefined
                      ? c.verdict
                      : `${c.peak.slice(1)} ${c.unit} (${c.verdict})`
                  }.\n`
                  + 'Click to isolate this channel — it takes the whole instrument. '
                  + 'Shift-click to add it to the ones already drawn, on the same x-axis.'
                }
                onClick={(e) => onToggleChannel(c.key, e.shiftKey || e.metaKey || e.ctrlKey)}
              >
                <StatusDot
                  tone={levelTone(c.verdict)}
                  hollow={c.verdict === 'unknown'}
                />
                <span className={styles.railLabel}>{c.label}</span>
                {/* THREE FACTS, THREE COLUMNS, ONE 22px ROW.

                    The dot is the window's worst level; the arrowed number is
                    the reading that earned it; the right-hand number is the
                    cursor's own value with the cursor's own verdict. All
                    three are needed — a battery that hit 8% an hour ago is a
                    fact about this window even while the cursor stands
                    somewhere healthy — and the middle one is what keeps the
                    dot from being the only place the excursion is stated. */}
                {c.peak !== undefined && (
                  <span className={styles.railPeak} data-tone={levelTone(c.verdict)}>
                    {c.peak}
                  </span>
                )}
                <span
                  className={styles.railValue}
                  data-known={c.value !== NO_VALUE}
                  data-tone={levelTone(c.level)}
                  data-stale={c.stale}
                >
                  {c.value}
                  {c.value !== NO_VALUE && (
                    <Unit unit={c.unit} className={styles.railUnit} />
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
        {/* THE COLUMN LEGEND, PRINTED — never a tooltip that has to be
            summoned (§8's bonus tells), and never two lines where one will
            do. The dot and the arrowed number are one fact stated twice
            (the window's worst), the right-hand number is the cursor's; the
            second line is §6's rule that an affordance prints its own
            shortcut, with the keys in mono because they are keys. */}
        <p className={styles.railHint}>
          {'dot · '}
          <span className={styles.railArrow}>↓↑</span>
          {' = worst in window'}
        </p>
        <p className={styles.railHint}>
          {'click isolates · '}
          <kbd className={styles.railKey}>⇧</kbd>
          {' adds'}
        </p>
      </Section>

      <Section
        label="Robots"
        aside={
          <span className={styles.railCount}>
            <Figure of={read} value={robots.length} />
          </span>
        }
      >
        <ul className={styles.railList}>
          {robots.map((r) => (
            <li key={r.id} className={styles.robotLi}>
              <button
                type="button"
                className={styles.railRow}
                data-shown={r.shown}
                aria-pressed={r.shown}
                disabled={r.locked}
                title={
                  `${r.id} — ${r.liveness}.`
                  + (r.age === undefined
                    ? ''
                    : ` Newest reading in this window is ${r.age} old.`)
                  + (r.locked
                    ? ' The only robot on the instrument; add another to compare'
                      + ' before hiding this one.'
                    : ' Toggles this robot in every plot.')
                }
                onClick={() => onToggleRobot(r.id)}
              >
                {robots.length > 1 ? (
                  <span className={styles.swatch} style={{ background: r.colour }} />
                ) : (
                  <StatusDot tone={r.tone} hollow={r.hollow} />
                )}
                <span className={styles.railId}>{r.id}</span>
                {r.age !== undefined && (
                  <span className={styles.railAge}>{r.age}</span>
                )}
                <span className={styles.railLive} data-tone={r.tone}>{r.liveness}</span>
              </button>
              {!r.primary && (
                <button
                  type="button"
                  className={styles.railDrop}
                  aria-label={`Remove ${r.id} from the comparison`}
                  onClick={() => onRemoveRobot(r.id)}
                >
                  <Icon name="close" size={12} />
                </button>
              )}
            </li>
          ))}
        </ul>
        <div className={styles.railAdd}>{adder}</div>
      </Section>

      {/* §13 tell 23: provenance that stops at the robot name is not
          provenance. Three blocks, and they answer three different questions:
          what was true at the instant under the cursor, what the window as a
          whole delivered, and what produced any of it. */}
      {groups.map((g) => (
        <Section
          key={g.key}
          label={g.label}
          aside={<span className={styles.railCount}>{g.aside}</span>}
        >
          <ul className={styles.railList}>
            {g.facts.map((f) => (
              <li key={f.key}>
                <div
                  className={styles.factRow}
                  data-wide={f.wide === true}
                  title={f.title}
                >
                  <span className={styles.railLabel}>{f.label}</span>
                  <span
                    className={styles.railValue}
                    data-tone={f.tone ?? 'neutral'}
                    data-suffix={f.suffix !== undefined}
                  >
                    <span>{f.value}</span>
                    {f.suffix !== undefined && (
                      <span className={styles.factSuffix}>{f.suffix}</span>
                    )}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </Section>
      ))}
    </div>
  );
}
